// The per-routine ACTION CONTROLS for the Scheduler (Signal re-layout) — Run now, Pause/Resume, Remove.
//
// Split out of RoutineList.tsx when pause/resume arrived, so the list row and the detail panel offer the
// SAME control with the same busy discipline rather than two near-copies that could drift apart.
//
// PAUSE AND RESUME WERE UNREACHABLE. `scheduler.pauseSchedule` / `resumeSchedule` have existed since
// Stage F — human-only, carded, negative-tested — with no route and no control, so `paused` was a state
// the board could display and no human could reach or leave. The owner asked for pause explicitly.
//
// A CLICK IS THE WHOLE INTERACTION. Nothing pends, nothing queues, nobody signs it off — pausing a
// routine is reversible and the owning module owns the decision. The control is offered from the recorded
// lifecycle state alone (active offers Pause, paused offers Resume): no client-side authority pre-check
// runs, because the OWNING module is the boundary (G-180) and its refusal note is what the list surfaces.
// The dashboard sets no state field of its own (P-GATE) — it routes, and the board re-reads.
//
// Tokens only (G-181); every control carries the routine NAME in its accessible label, never its id
// (G-197).

import { useState } from 'react';
import type { ScheduleHealthScheduleRow } from '../projections.ts';
import type { RouteResult } from '../api-client.ts';
import { Btn } from '../primitives.tsx';

/** A routed per-routine action: the parent re-reads the board on ok (G-182) and returns the result. */
export type RoutineRoute = (scheduleId: string) => Promise<RouteResult>;

/** The routed actions a routine can be offered. Which ones RENDER depends on its recorded state. */
export interface RoutineRoutes {
  readonly onRunNow: RoutineRoute;
  readonly onPause: RoutineRoute;
  readonly onResume: RoutineRoute;
  readonly onRemove: RoutineRoute;
  /** RESTORE an archived routine to paused (owner ruling 2 Sep 2026) — the human's way back. */
  readonly onRestore: RoutineRoute;
}

/** Route once, with an in-flight guard so a double-click cannot double-route (the GateRow busy discipline). */
function useOneShot(
  route: RoutineRoute,
  scheduleId: string,
): {
  readonly busy: boolean;
  readonly go: () => void;
} {
  const [busy, setBusy] = useState(false);
  return {
    busy,
    go: () => {
      if (busy) return;
      setBusy(true);
      void route(scheduleId).finally(() => setBusy(false));
    },
  };
}

/** The Run-now control for a LIVE routine — the mechanism refuses any other lifecycle state, so no dead
 *  control is rendered for a paused or draft one (the honest-affordance posture). */
export function RunNowAction({
  row,
  onRunNow,
}: {
  readonly row: ScheduleHealthScheduleRow;
  readonly onRunNow: RoutineRoute;
}): React.JSX.Element {
  const { busy, go } = useOneShot(onRunNow, row.scheduleId);
  return (
    <Btn kind="ghost" sm disabled={busy} ariaLabel={`Run now: ${row.name}`} onClick={go}>
      Run now
    </Btn>
  );
}

/** The word the toggle carries for a routine's recorded state — null when the state offers neither. */
function toggleFor(
  state: ScheduleHealthScheduleRow['state'],
): { readonly label: 'Pause' | 'Resume'; readonly leg: 'pause' | 'resume' } | null {
  if (state === 'active') return { label: 'Pause', leg: 'pause' };
  if (state === 'paused') return { label: 'Resume', leg: 'resume' };
  // A `draft` has never been activated, a `finished` one has exhausted its cadence, and an `archived` one
  // comes back through Restore: the module phase-guards all three, so offering either word would be
  // offering a control that can only refuse.
  return null;
}

/** The RESTORE control — offered on an ARCHIVED routine only. It comes back PAUSED (the module's rule), so
 *  the person then resumes it deliberately; the archive reason stays on the record. */
export function RestoreAction({
  row,
  onRestore,
}: {
  readonly row: ScheduleHealthScheduleRow;
  readonly onRestore: RoutineRoute;
}): React.JSX.Element | null {
  const { busy, go } = useOneShot(onRestore, row.scheduleId);
  if (row.state !== 'archived') return null;
  return (
    <Btn kind="quiet" sm disabled={busy} ariaLabel={`Restore routine: ${row.name}`} onClick={go}>
      {busy ? 'Restoring…' : 'Restore'}
    </Btn>
  );
}

/** The PAUSE / RESUME toggle — one control whose word follows the routine's RECORDED state. */
export function LifecycleToggleAction({
  row,
  onPause,
  onResume,
}: {
  readonly row: ScheduleHealthScheduleRow;
  readonly onPause: RoutineRoute;
  readonly onResume: RoutineRoute;
}): React.JSX.Element | null {
  const toggle = toggleFor(row.state);
  const { busy, go } = useOneShot(toggle?.leg === 'resume' ? onResume : onPause, row.scheduleId);
  if (toggle === null) return null;
  return (
    <Btn
      kind="quiet"
      sm
      disabled={busy}
      ariaLabel={`${toggle.label} routine: ${row.name}`}
      onClick={go}
    >
      {toggle.label}
    </Btn>
  );
}

/** The Remove control, offered on EVERY routine. Two steps, because removal has no undo — but two
 *  CLICKS, not an approval queue: the owner asked to clear his own board without asking anybody
 *  (2026-08-02). The owning module re-checks the human authority; a refusal comes back as the list's
 *  note.
 *
 *  It used to render only for a routine with no runs. That looked like the honest-affordance posture
 *  and was the opposite: an ACTIVE routine fires within a minute of being created, so the control
 *  vanished almost immediately — and worse, `recentRuns` is a bounded recent window, so the button was
 *  still shown for a routine the server would refuse. The owner hit exactly that (3 Aug 2026): clicked
 *  Remove, then "Remove for good", and the row came back. Removing a routine now deletes its row and
 *  leaves its record alone, so there is nothing left to gate on. */
export function RemoveAction({
  row,
  onRemove,
}: {
  readonly row: ScheduleHealthScheduleRow;
  readonly onRemove: RoutineRoute;
}): React.JSX.Element {
  // THE ARM IS REMEMBERED AGAINST THE ROUTINE IT WAS ARMED FOR, never as a bare flag. A bare flag belongs
  // to the POSITION in the tree, so any caller that re-renders this control in place with a different
  // routine hands the new one a live "Remove for good" the human never armed — one click from deleting the
  // wrong routine, with no undo. Holding the id makes that impossible wherever the control is mounted,
  // rather than resting on every present and future caller keying it correctly.
  const [armedFor, setArmedFor] = useState<string | null>(null);
  const arming = armedFor === row.scheduleId;
  const { busy, go } = useOneShot(onRemove, row.scheduleId);
  if (!arming) {
    return (
      <Btn
        kind="quiet"
        sm
        ariaLabel={`Remove routine: ${row.name}`}
        onClick={() => setArmedFor(row.scheduleId)}
      >
        Remove
      </Btn>
    );
  }
  return (
    <>
      <Btn
        kind="quiet"
        sm
        disabled={busy}
        ariaLabel={`Keep routine: ${row.name}`}
        onClick={() => setArmedFor(null)}
      >
        Keep
      </Btn>
      <Btn
        kind="ghost"
        sm
        disabled={busy}
        ariaLabel={`Confirm remove routine: ${row.name}`}
        onClick={go}
      >
        {busy ? 'Removing…' : 'Remove for good'}
      </Btn>
    </>
  );
}

/** The full action cluster for one routine row — Run now (live only), the lifecycle toggle, Remove. */
export function RoutineActions({
  row,
  routes,
}: {
  readonly row: ScheduleHealthScheduleRow;
  readonly routes: RoutineRoutes;
}): React.JSX.Element {
  return (
    <>
      {row.state === 'active' ? <RunNowAction row={row} onRunNow={routes.onRunNow} /> : null}
      <LifecycleToggleAction row={row} onPause={routes.onPause} onResume={routes.onResume} />
      <RestoreAction row={row} onRestore={routes.onRestore} />
      <RemoveAction row={row} onRemove={routes.onRemove} />
    </>
  );
}
